import React, { useState } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import { MODELS, TONES } from '../utils/constants';

/* ─────────────────── Collapsible Section ─────────────────── */
function Section({ title, subtitle, expanded, onToggle, children }) {
    return (
        <div style={{ borderBottom: '1px solid var(--border)' }}>
            <button
                onClick={onToggle}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
            >
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--navy)' }}>{title}</div>
                    {subtitle && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{subtitle}</div>}
                </div>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)', transition: 'transform .2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}>▶</span>
            </button>
            {expanded && <div style={{ paddingBottom: '16px' }}>{children}</div>}
        </div>
    );
}

/* ─────────── Inline Confirm (replaces window.confirm) ─────────── */
function InlineConfirm({ message, onConfirm, onCancel }) {
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
            <div style={{
                background: '#fff', borderRadius: '10px', padding: '20px', maxWidth: '280px', width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}>
                <div style={{ fontSize: '13px', color: 'var(--navy)', marginBottom: '16px', lineHeight: '1.4' }}>{message}</div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={onConfirm} style={{ background: 'var(--orange)', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', color: '#fff', fontWeight: 'bold' }}>Delete</button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────── Toast (replaces alert) ─────────────────── */
function Toast({ message, type, onDone }) {
    React.useEffect(() => {
        const t = setTimeout(onDone, 2400);
        return () => clearTimeout(t);
    }, [onDone]);

    return (
        <div style={{
            position: 'fixed', bottom: '72px', left: '50%', transform: 'translateX(-50%)',
            background: type === 'error' ? '#c0392b' : 'var(--navy)', color: '#fff',
            padding: '8px 16px', borderRadius: '8px', fontSize: '12px', zIndex: 9999,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)', whiteSpace: 'nowrap',
        }}>
            {message}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
   SETTINGS PANE — accordion-based, expandable settings
   ═══════════════════════════════════════════════════════════════ */
export default function SettingsPane({ onClose, onSave }) {
    const { userSettings, userEmail } = useAddin();

    // Which sections are expanded
    const [openSections, setOpenSections] = useState({ models: true });
    const toggle = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

    // Sync status
    const [syncing, setSyncing] = useState(false);

    // Toast
    const [toast, setToast] = useState(null);
    const showToast = (message, type = 'info') => setToast({ message, type });

    // Inline confirm
    const [confirmState, setConfirmState] = useState(null);
    const requestConfirm = (message) => new Promise((resolve) => {
        setConfirmState({ message, resolve });
    });
    const handleConfirmYes = () => { confirmState?.resolve(true); setConfirmState(null); };
    const handleConfirmNo = () => { confirmState?.resolve(false); setConfirmState(null); };

    // ── Models ──
    const [customModels, setCustomModels] = useState(userSettings.customModels || MODELS);
    const [defaultModel, setDefaultModel] = useState(userSettings.defaultModel || 'claude-sonnet');
    const [editingModelId, setEditingModelId] = useState(null);
    const [modelForm, setModelForm] = useState({ id: '', provider: '', name: '', desc: '', apiId: '' });

    // ── Templates ──
    const [taskTemplates, setTaskTemplates] = useState(userSettings.taskTemplates || []);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [templateForm, setTemplateForm] = useState({ id: '', name: '', description: '', priority: 'Normal', time_estimated: '' });

    // ── Instructions ──
    const existingInstructions = userSettings.instructions || {};
    const [generalInstructions, setGeneralInstructions] = useState(existingInstructions.general || '');
    const [toneOverrides, setToneOverrides] = useState(
        existingInstructions.toneOverrides || TONES.reduce((acc, t) => ({ ...acc, [t]: '' }), {})
    );

    // ═══════ Save All ═══════
    const handleSaveSettings = () => {
        onSave({
            customModels,
            defaultModel,
            taskTemplates,
            instructions: {
                general: generalInstructions,
                toneOverrides,
            },
        });
        onClose();
    };

    // ═══════ Model helpers ═══════
    const startAddModel = () => {
        const newId = 'model-' + Date.now();
        setEditingModelId(newId);
        setModelForm({ id: newId, provider: 'OpenAI', name: '', desc: '', apiId: '', isNew: true });
    };
    const startEditModel = (model) => { setEditingModelId(model.id); setModelForm({ ...model }); };
    const cancelEditModel = () => setEditingModelId(null);

    const saveModelForm = () => {
        if (!modelForm.name.trim() || !modelForm.apiId.trim()) {
            showToast('Name and API ID are required.', 'error');
            return;
        }
        setCustomModels(prev => {
            if (modelForm.isNew) {
                const { isNew, ...newModel } = modelForm;
                newModel.accentColor = newModel.provider === 'OpenAI' ? '#10A37F' : 'var(--teal)';
                return [...prev, newModel];
            }
            return prev.map(m => m.id === modelForm.id ? { ...m, ...modelForm } : m);
        });
        setEditingModelId(null);
    };

    const deleteModel = async (id) => {
        if (customModels.length === 1) {
            showToast('You must have at least one model.', 'error');
            return;
        }
        const yes = await requestConfirm('Delete this model?');
        if (!yes) return;
        setCustomModels(prev => prev.filter(m => m.id !== id));
        if (defaultModel === id) {
            const next = customModels.filter(m => m.id !== id);
            setDefaultModel(next[0]?.id);
        }
    };

    const moveModel = (index, direction) => {
        if (index + direction < 0 || index + direction >= customModels.length) return;
        const arr = [...customModels];
        [arr[index], arr[index + direction]] = [arr[index + direction], arr[index]];
        setCustomModels(arr);
    };

    // ═══════ Template helpers ═══════
    const startAddTemplate = () => {
        const newId = 'tpl-' + Date.now();
        setEditingTemplateId(newId);
        setTemplateForm({ id: newId, name: '', description: '', priority: 'Normal', time_estimated: '', isNew: true });
    };
    const startEditTemplate = (tpl) => { setEditingTemplateId(tpl.id); setTemplateForm({ ...tpl }); };
    const cancelEditTemplate = () => setEditingTemplateId(null);

    const saveTemplateForm = () => {
        if (!templateForm.name.trim()) {
            showToast('Template name is required.', 'error');
            return;
        }
        setTaskTemplates(prev => {
            if (templateForm.isNew) {
                const { isNew, ...newTpl } = templateForm;
                return [...prev, newTpl];
            }
            return prev.map(t => t.id === templateForm.id ? { ...t, ...templateForm } : t);
        });
        setEditingTemplateId(null);
    };

    const deleteTemplate = async (id) => {
        const yes = await requestConfirm('Delete this template?');
        if (!yes) return;
        setTaskTemplates(prev => prev.filter(t => t.id !== id));
    };

    const moveTemplate = (index, direction) => {
        if (index + direction < 0 || index + direction >= taskTemplates.length) return;
        const arr = [...taskTemplates];
        [arr[index], arr[index + direction]] = [arr[index + direction], arr[index]];
        setTaskTemplates(arr);
    };

    // ═══════ Cloud Sync ═══════
    const handleBackup = async () => {
        if (!userEmail) { showToast('User email required to sync.', 'error'); return; }
        setSyncing(true);
        try {
            await webhook.syncSettings({
                userEmail,
                action: 'backup',
                settings: {
                    customModels, defaultModel, taskTemplates,
                    instructions: { general: generalInstructions, toneOverrides },
                },
            });
            showToast('Settings backed up to cloud.');
        } catch (err) {
            console.error('Backup failed:', err);
            showToast(`Backup failed: ${err.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleRestore = async () => {
        if (!userEmail) return;
        const yes = await requestConfirm('Restore settings from cloud? This will overwrite your current settings.');
        if (!yes) return;

        setSyncing(true);
        try {
            const res = await webhook.syncSettings({ userEmail, action: 'restore' });
            if (res?.settings) {
                if (res.settings.customModels) setCustomModels(res.settings.customModels);
                if (res.settings.defaultModel) setDefaultModel(res.settings.defaultModel);
                if (res.settings.taskTemplates) setTaskTemplates(res.settings.taskTemplates);
                if (res.settings.instructions) {
                    setGeneralInstructions(res.settings.instructions.general || '');
                    setToneOverrides(res.settings.instructions.toneOverrides || TONES.reduce((acc, t) => ({ ...acc, [t]: '' }), {}));
                }
                showToast('Settings restored from cloud.');
            } else {
                showToast('No settings found in cloud.', 'error');
            }
        } catch (err) {
            console.error('Restore failed:', err);
            showToast(`Restore failed: ${err.message}`, 'error');
        } finally {
            setSyncing(false);
        }
    };

    // ═══════ Shared form renderers ═══════
    const renderModelForm = (form, setForm, onSave, onCancel) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
                <label style={labelStyle}>Display Name</label>
                <input type="text" className="form-input" placeholder="e.g. GPT-4o" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
                <label style={labelStyle}>Provider</label>
                <input type="text" className="form-input" placeholder="e.g. OpenAI" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} />
            </div>
            <div>
                <label style={labelStyle}>API ID</label>
                <input type="text" className="form-input" placeholder="e.g. gpt-4o" value={form.apiId} onChange={e => setForm({ ...form, apiId: e.target.value })} />
            </div>
            <div>
                <label style={labelStyle}>Description</label>
                <input type="text" className="form-input" placeholder="e.g. Strong instruction-following." value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                <button onClick={onCancel} style={linkBtnStyle}>Cancel</button>
                <button onClick={onSave} className="confirm-btn teal" style={{ padding: '4px 12px', margin: 0, width: 'auto', fontSize: '12px' }}>Save</button>
            </div>
        </div>
    );

    const renderTemplateForm = (form, setForm, onSave, onCancel) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input type="text" className="form-input" placeholder="Template Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <textarea className="form-input" placeholder="Default Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            <div style={{ display: 'flex', gap: '8px' }}>
                <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    <option value="Low">Low Priority</option>
                    <option value="Normal">Normal Priority</option>
                    <option value="High">High Priority</option>
                </select>
                <input type="number" className="form-input" placeholder="Est. Mins" value={form.time_estimated} onChange={e => setForm({ ...form, time_estimated: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                <button onClick={onCancel} style={linkBtnStyle}>Cancel</button>
                <button onClick={onSave} className="confirm-btn teal" style={{ padding: '4px 12px', margin: 0, width: 'auto', fontSize: '12px' }}>Save</button>
            </div>
        </div>
    );

    // ═══════ RENDER ═══════
    return (
        <div className="addin-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, color: 'var(--navy)' }}>Settings</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>&times;</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>

                {/* ──────── AI Models ──────── */}
                <Section title="AI Models" subtitle={`${customModels.length} model${customModels.length !== 1 ? 's' : ''} configured`} expanded={!!openSections.models} onToggle={() => toggle('models')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={startAddModel} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>+ Add Model</button>
                        </div>

                        {customModels.map((model, idx) => (
                            <div key={model.id} style={cardStyle(editingModelId === model.id)}>
                                {editingModelId === model.id
                                    ? renderModelForm(modelForm, setModelForm, saveModelForm, cancelEditModel)
                                    : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', color: 'var(--text-secondary)' }}>
                                                <span style={{ cursor: idx > 0 ? 'pointer' : 'default', opacity: idx > 0 ? 1 : 0.2 }} onClick={() => moveModel(idx, -1)}>▲</span>
                                                <span style={{ cursor: idx < customModels.length - 1 ? 'pointer' : 'default', opacity: idx < customModels.length - 1 ? 1 : 0.2 }} onClick={() => moveModel(idx, 1)}>▼</span>
                                            </div>
                                            <div style={{ flex: 1, paddingLeft: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--navy)' }}>{model.name}</span>
                                                    <span style={badgeStyle}>{model.provider}</span>
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}><code>{model.apiId}</code></div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                                    <span onClick={() => startEditModel(model)} style={{ cursor: 'pointer', color: 'var(--teal)' }}>Edit</span>
                                                    <span onClick={() => deleteModel(model.id)} style={{ cursor: 'pointer', color: 'var(--orange)' }}>Del</span>
                                                </div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer' }}>
                                                    <input type="radio" name="defaultModel" checked={defaultModel === model.id} onChange={() => setDefaultModel(model.id)} /> Default
                                                </label>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        ))}

                        {/* New model form (not yet in the list) */}
                        {editingModelId && modelForm.isNew && (
                            <div style={cardStyle(true)}>
                                {renderModelForm(modelForm, setModelForm, saveModelForm, cancelEditModel)}
                            </div>
                        )}
                    </div>
                </Section>

                {/* ──────── Task Templates ──────── */}
                <Section title="Task Templates" subtitle={`${taskTemplates.length} template${taskTemplates.length !== 1 ? 's' : ''}`} expanded={!!openSections.templates} onToggle={() => toggle('templates')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={startAddTemplate} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>+ Add Template</button>
                        </div>

                        {taskTemplates.length === 0 && !editingTemplateId && (
                            <div style={{ textAlign: 'center', padding: '24px 10px', color: '#8a8a8a', fontSize: '12px', background: '#f9f9f9', borderRadius: '8px' }}>
                                No task templates defined yet.
                            </div>
                        )}

                        {taskTemplates.map((tpl, idx) => (
                            <div key={tpl.id} style={cardStyle(editingTemplateId === tpl.id)}>
                                {editingTemplateId === tpl.id
                                    ? renderTemplateForm(templateForm, setTemplateForm, saveTemplateForm, cancelEditTemplate)
                                    : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', color: 'var(--text-secondary)' }}>
                                                <span style={{ cursor: idx > 0 ? 'pointer' : 'default', opacity: idx > 0 ? 1 : 0.2 }} onClick={() => moveTemplate(idx, -1)}>▲</span>
                                                <span style={{ cursor: idx < taskTemplates.length - 1 ? 'pointer' : 'default', opacity: idx < taskTemplates.length - 1 ? 1 : 0.2 }} onClick={() => moveTemplate(idx, 1)}>▼</span>
                                            </div>
                                            <div style={{ flex: 1, paddingLeft: '8px' }}>
                                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--navy)' }}>{tpl.name}</div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.description || 'No description'}</div>
                                                <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                                    {tpl.priority !== 'Normal' && <span style={{ ...badgeStyle, color: tpl.priority === 'High' ? 'var(--orange)' : '#666' }}>{tpl.priority}</span>}
                                                    {tpl.time_estimated && <span style={badgeStyle}>{tpl.time_estimated}m</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                                                <span onClick={() => startEditTemplate(tpl)} style={{ cursor: 'pointer', color: 'var(--teal)' }}>Edit</span>
                                                <span onClick={() => deleteTemplate(tpl.id)} style={{ cursor: 'pointer', color: 'var(--orange)' }}>Del</span>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        ))}

                        {editingTemplateId && templateForm.isNew && (
                            <div style={cardStyle(true)}>
                                {renderTemplateForm(templateForm, setTemplateForm, saveTemplateForm, cancelEditTemplate)}
                            </div>
                        )}
                    </div>
                </Section>

                {/* ──────── Instructions ──────── */}
                <Section title="AI Instructions" subtitle="Firm context & tone-specific guidance" expanded={!!openSections.instructions} onToggle={() => toggle('instructions')}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                        {/* General / Firm Instructions */}
                        <div>
                            <label style={{ ...labelStyle, fontSize: '12px', fontWeight: 'bold' }}>General Instructions</label>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                                Firm history, background, house style, recurring preferences. Applied to every generation.
                            </div>
                            <textarea
                                className="form-input"
                                style={textareaStyle}
                                placeholder="e.g. We are a mid-sized corporate law firm specializing in M&A. Always include our disclaimer footer. Prefer Canadian spelling..."
                                value={generalInstructions}
                                onChange={e => setGeneralInstructions(e.target.value)}
                                rows={4}
                            />
                        </div>

                        {/* Per-tone overrides */}
                        <div>
                            <label style={{ ...labelStyle, fontSize: '12px', fontWeight: 'bold' }}>Tone-Specific Instructions</label>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                Additional instructions applied when a specific tone is selected in the draft generator.
                            </div>

                            {TONES.map(tone => (
                                <div key={tone} style={{ marginBottom: '10px' }}>
                                    <label style={{ ...labelStyle, fontWeight: '600' }}>{tone}</label>
                                    <textarea
                                        className="form-input"
                                        style={textareaStyle}
                                        placeholder={tonePlaceholders[tone] || `Instructions for ${tone} tone...`}
                                        value={toneOverrides[tone] || ''}
                                        onChange={e => setToneOverrides(prev => ({ ...prev, [tone]: e.target.value }))}
                                        rows={2}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* ──────── Cloud Sync ──────── */}
                <Section title="Cloud Sync" subtitle="Backup & restore all settings" expanded={!!openSections.sync} onToggle={() => toggle('sync')}>
                    <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                        Backup and restore all personalization — models, templates, and instructions — to the master database.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="draft-btn secondary" onClick={handleBackup} disabled={syncing} style={{ flex: 1, padding: '8px', fontSize: '12px' }}>
                            {syncing ? 'Syncing...' : '↑ Backup'}
                        </button>
                        <button className="draft-btn secondary" onClick={handleRestore} disabled={syncing} style={{ flex: 1, padding: '8px', fontSize: '12px' }}>
                            {syncing ? 'Syncing...' : '↓ Restore'}
                        </button>
                    </div>
                </Section>

            </div>

            {/* Save & Close — always visible at bottom */}
            <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                <button className="action-btn full primary" onClick={handleSaveSettings} style={{ width: '100%', justifyContent: 'center', padding: '12px', minHeight: '44px' }}>
                    Save &amp; Close
                </button>
            </div>

            {/* Overlays */}
            {confirmState && <InlineConfirm message={confirmState.message} onConfirm={handleConfirmYes} onCancel={handleConfirmNo} />}
            {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
        </div>
    );
}

/* ── Style constants ── */
const labelStyle = { fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px', display: 'block' };
const linkBtnStyle = { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' };
const badgeStyle = { fontSize: '10px', background: '#f0f0f0', borderRadius: '4px', padding: '2px 6px', color: '#666' };
const textareaStyle = { width: '100%', minHeight: '48px', borderRadius: '6px', padding: '8px', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' };
const cardStyle = (editing) => ({ background: '#fff', borderRadius: '8px', border: editing ? '1px solid var(--teal)' : '1px solid var(--border)', padding: '12px' });

const tonePlaceholders = {
    'Formal': 'e.g. Use "Dear Mr./Ms." salutations. Avoid contractions. Cite clause numbers explicitly.',
    'Neutral': 'e.g. Professional but approachable. Use first names after initial greeting.',
    'Client-Friendly': 'e.g. Warm, empathetic. Avoid legal jargon where possible. Explain terms in plain language.',
};
